import api from './api';

// ---------------------------------------------------------------------------
// Shared filter params
// ---------------------------------------------------------------------------

export interface HouseholdFilterParams {
  q?: string;
  district_code?: string;
  prefecture_code?: string;
  locality?: string;
  assigned_chw_id?: number;
  has_pregnant?: boolean;
  has_under_5?: boolean;
  has_nutrition_risk?: boolean;
  period_start?: string;
  period_end?: string;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export interface OverviewKpis {
  total_households_visited: number;
  total_deaths_reported: number;
  average_household_size: number;
  suspected_illness_reported: number;
  illness_confirmed: number;
}

export interface GenderSlice {
  id: string;
  label: string;
  value: number;
}

export interface DemographicsOut {
  average_household_size: number;
  gender_distribution: GenderSlice[];
}

export interface OverviewResponse {
  kpis: OverviewKpis;
  demographics: DemographicsOut;
  map: Record<string, any>;
  filters_echo: Record<string, any>;
}

export interface FilterMetaOut {
  localities: string[];
  districts: string[];
  prefectures: string[];
  aires: string[];
}

// ---------------------------------------------------------------------------
// Households
// ---------------------------------------------------------------------------

export interface HouseholdListItem {
  id: number;
  external_code: string;
  house_number?: string;
  registry_code?: string;
  head_name: string;
  locality?: string;
  village_neighborhood?: string;
  district_code?: string;
  assigned_chw_id?: number;
  assigned_chw_name?: string;
  member_count: number;
  under_5_count: number;
  pregnant_count: number;
  latitude?: number;
  longitude?: number;
  registered_at?: string;
  last_visit_at?: string;
}

export interface HouseholdListResponse {
  items: HouseholdListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface GpsOut {
  latitude?: number;
  longitude?: number;
}

export interface CompositionOut {
  household_members: number;
  adults_18_plus: number;
  children_0_17: number;
  children_under_5: number;
  pregnant_women: number;
  lactating_women: number;
  elderly_60_plus: number;
}

export interface MchSummaryOut {
  pregnant_women: Record<string, any>;
  anc_status: string;
  total_children: number;
  fully_immunized_0_23m: Record<string, any>;
  nutrition_screening_u5: Record<string, any>;
  danger_sign_screening: Record<string, any>;
  next_anc_appointment?: string;
}

export interface NoteOut {
  id: number;
  household_id: number;
  body: string;
  recorded_by_user_id?: number;
  recorded_by_name?: string;
  recorded_at: string;
}

export interface MemberOut {
  id: number;
  household_id: number;
  full_name: string;
  sex: string;
  date_of_birth?: string;
  age_years?: number;
  id_number?: string;
  role_in_hh: string;
  is_pregnant: boolean;
  is_lactating: boolean;
  is_under_5: boolean;
  is_elderly_60: boolean;
  fully_immunized_0_23m?: boolean;
  muac_cm?: number;
  nutrition_at_risk: boolean;
  has_danger_signs: boolean;
  attending_anc: boolean;
  next_anc_date?: string;
  death_reported: boolean;
  died_at?: string;
  suspected_illness: boolean;
  confirmed_illness: boolean;
  illness_type?: string;
}

export interface AssignedChwCardOut {
  id: number;
  chw_code: string;
  name?: string;
  phone_number?: string;
  aire_fosa?: string;
}

export interface HouseholdDetailResponse {
  id: number;
  external_code: string;
  house_number?: string;
  registry_code?: string;
  photo_url?: string;
  head_name: string;
  locality?: string;
  village_neighborhood?: string;
  site_name?: string;
  gps: GpsOut;
  registered_at?: string;
  address_text?: string;
  assigned_chw?: AssignedChwCardOut;
  composition: CompositionOut;
  mch: MchSummaryOut;
  notes: NoteOut[];
  members: MemberOut[];
}

// ---------------------------------------------------------------------------
// Household create (Add Household form)
// ---------------------------------------------------------------------------

export interface MemberCreatePayload {
  full_name: string;
  sex: 'male' | 'female';
  age_years: number;
  id_number?: string;
  role_in_hh: 'head' | 'member';
  is_pregnant?: boolean;
  is_lactating?: boolean;
  fully_immunized_0_23m?: boolean;
  muac_cm?: number;
  nutrition_at_risk?: boolean;
  has_danger_signs?: boolean;
  attending_anc?: boolean;
  next_anc_date?: string;
  death_reported?: boolean;
  died_at?: string;
  suspected_illness?: boolean;
  confirmed_illness?: boolean;
  illness_type?: string;
}

export type MemberUpdatePayload = Partial<MemberCreatePayload>;

export interface HouseholdCreatePayload {
  house_number: string;
  head_name: string;
  registry_code?: string;
  members: MemberCreatePayload[];
}

export interface HouseholdUpdatePayload {
  house_number?: string;
  head_name?: string;
  locality?: string;
  village_neighborhood?: string;
  site_name?: string;
  prefecture_code?: string;
  district_code?: string;
  address_text?: string;
  assigned_chw_id?: number;
  is_active?: boolean;
}

// ---------------------------------------------------------------------------
// Registry codes (QR print-and-scan)
// ---------------------------------------------------------------------------

export interface RegistryCodeOut {
  id: number;
  code: string;
  is_used: boolean;
}

export interface CodeLookupResponse {
  code: string;
  valid: boolean;
  is_used: boolean;
  message?: string;
}

// ---------------------------------------------------------------------------
// CHW performance
// ---------------------------------------------------------------------------

export interface ChwPerformanceSummary {
  total_chws_active: number;
  households_visited: number;
  households_assigned: number;
  services_provided: number;
  average_coverage_pct: number;
  period_start?: string;
  period_end?: string;
}

export interface ChwPerformanceRow {
  chw_id: number;
  chw_code: string;
  name?: string;
  aire_fosa?: string;
  households_assigned: number;
  households_visited: number;
  coverage_pct: number;
  performance_status: string;
  pregnancies_followed: number;
  children_u5_served: number;
  anc_referrals: number;
  home_visits: number;
  services_provided: number;
  last_activity_at?: string;
}

export interface ChwPerformanceListResponse {
  items: ChwPerformanceRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface ChwDetailResponse {
  profile: {
    id: number;
    account_id: number;
    user_id: number;
    chw_code: string;
    phone_number?: string;
    aire_fosa?: string;
    catchment_area?: string;
    district_code?: string;
    prefecture_code?: string;
    is_active: boolean;
    households_assigned_override?: number;
    name?: string;
  };
  performance: ChwPerformanceRow;
  assigned_households: HouseholdListItem[];
}

// ---------------------------------------------------------------------------
// App sessions (Android app logins only — never web) — super admin only
// ---------------------------------------------------------------------------

export interface AppSessionOut {
  user_id: number;
  email: string;
  name: string;
  role: string;
  is_self: boolean;
  session_started_at?: string;
}

export interface AppSessionListResponse {
  items: AppSessionOut[];
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

const BASE = '/community-registry';

function toQuery(params: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  });
  return out;
}

export const communityRegistryApi = {
  getOverview: (filters: HouseholdFilterParams = {}) =>
    api.get<OverviewResponse>(`${BASE}/overview`, { params: toQuery(filters) }).then((r) => r.data),

  getFilterMeta: () => api.get<FilterMetaOut>(`${BASE}/meta/filters`).then((r) => r.data),

  listHouseholds: (
    filters: HouseholdFilterParams = {},
    page = 1,
    pageSize = 20,
    sortBy = 'registered_at',
    sortDir = 'desc',
  ) =>
    api
      .get<HouseholdListResponse>(`${BASE}/households`, {
        params: toQuery({ ...filters, page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir }),
      })
      .then((r) => r.data),

  getHousehold: (id: number) =>
    api.get<HouseholdDetailResponse>(`${BASE}/households/${id}`).then((r) => r.data),

  createHousehold: (payload: HouseholdCreatePayload) =>
    api.post<HouseholdDetailResponse>(`${BASE}/households`, payload).then((r) => r.data),

  updateHousehold: (id: number, payload: HouseholdUpdatePayload) =>
    api.patch<HouseholdDetailResponse>(`${BASE}/households/${id}`, payload).then((r) => r.data),

  addMember: (householdId: number, payload: MemberCreatePayload) =>
    api.post<MemberOut>(`${BASE}/households/${householdId}/members`, payload).then((r) => r.data),

  updateMember: (memberId: number, payload: MemberUpdatePayload) =>
    api.patch<MemberOut>(`${BASE}/members/${memberId}`, payload).then((r) => r.data),

  deleteMember: (memberId: number) => api.delete(`${BASE}/members/${memberId}`),

  addNote: (householdId: number, body: string) =>
    api.post<NoteOut>(`${BASE}/households/${householdId}/notes`, { body }).then((r) => r.data),

  exportHouseholdsUrl: (filters: HouseholdFilterParams = {}) => {
    const params = new URLSearchParams(toQuery(filters) as any).toString();
    return `${(api.defaults.baseURL || '').replace(/\/$/, '')}${BASE}/households/export${params ? `?${params}` : ''}`;
  },

  listChws: () => api.get(`${BASE}/chws`).then((r) => r.data),

  chwPerformanceSummary: (periodStart?: string, periodEnd?: string) =>
    api
      .get<ChwPerformanceSummary>('/chw-performance/summary', {
        params: toQuery({ period_start: periodStart, period_end: periodEnd }),
      })
      .then((r) => r.data),

  chwPerformanceList: (
    page = 1,
    pageSize = 10,
    periodStart?: string,
    periodEnd?: string,
    sortBy?: string,
  ) =>
    api
      .get<ChwPerformanceListResponse>('/chw-performance', {
        params: toQuery({ page, page_size: pageSize, period_start: periodStart, period_end: periodEnd, sort_by: sortBy }),
      })
      .then((r) => r.data),

  chwPerformanceExportUrl: () => {
    return `${(api.defaults.baseURL || '').replace(/\/$/, '')}/chw-performance/export`;
  },

  chwDetail: (chwId: number) =>
    api.get<ChwDetailResponse>(`/chw-performance/${chwId}`).then((r) => r.data),

  generateRegistryCodes: (count: number) =>
    api
      .post<{ codes: RegistryCodeOut[] }>(`${BASE}/registry-codes/generate`, { count })
      .then((r) => r.data.codes),

  lookupRegistryCode: (code: string) =>
    api
      .get<CodeLookupResponse>(`${BASE}/registry-codes/${encodeURIComponent(code)}/lookup`)
      .then((r) => r.data),

  downloadRegistryCodesPdf: (codes: string[]) =>
    api
      .post(`${BASE}/registry-codes/pdf`, { codes }, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  listAppSessions: () => api.get<AppSessionListResponse>('/app-sessions/').then((r) => r.data),

  terminateAppSession: (userId: number) =>
    api.post(`/app-sessions/${userId}/terminate`).then((r) => r.data),
};

export default communityRegistryApi;
